import { useState, useMemo, useRef } from 'react';
import { Button, Box, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ConfigEntryType } from 'config/config';
import { useFormContext, useWatch } from 'react-hook-form';
import { useConfigQuery } from '@buildwise/libs/configurations';
import useUserData from 'hooks/useUserData';
import Popper from 'components/common/popper/Popper';
import { styled } from '@mui/material/styles';
import { theme } from 'config/theme';
import StatusChip from '../Utils/StatusChip';

function TaskStatusSelectField({ field }: { field: ConfigEntryType }) {
  const { companyId } = useUserData();
  const configName = field.referenceObject?.configRefName || field.entryCode;
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext();
  const value = useWatch({ control, name: field.entryCode });

  const { activeUniqueEntries = [] } = useConfigQuery({
    companyId,
    configName,
  });

  const options = useMemo(
    () =>
      activeUniqueEntries.map((entry: ConfigEntryType) => ({
        label: entry.entryDescription || entry.entryCode,
        value: entry.entryCode,
        color: entry.referenceObject?.color,
      })),
    [activeUniqueEntries],
  );

  const selectedStatus = options.find((option) => option.value === value);

  const [open, setOpen] = useState(false);
  const anchorEl = useRef<HTMLButtonElement | null>(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleSelect = (entryCode: string) => {
    setValue(field.entryCode, entryCode, { shouldDirty: true, shouldValidate: true });
    handleClose();
  };

  return (
    <Box>
      <Button
        ref={anchorEl}
        variant="contained"
        onClick={handleOpen}
        endIcon={<ArrowDropDownIcon />}
        disabled={isSubmitting}
        sx={{
          color: theme.palette.custom.tuna,
          fontSize: 15,
          fontWeight: 600,
          backgroundColor: selectedStatus?.color || theme.palette.custom.dairyCream,
          '&:hover': {
            backgroundColor: selectedStatus?.color || theme.palette.custom.paleGold,
            boxShadow: 'none',
          },
          borderRadius: 1,
          px: 1.5,
          py: 0.5,
          height: 'fit-content',
          minWidth: 'fit-content',
          textTransform: 'none',
          boxShadow: 'none',
        }}
      >
        {selectedStatus?.label}
      </Button>
      <Popper
        open={open}
        onClose={handleClose}
        anchor={anchorEl.current}
        placement="bottom-start"
        arrow={false}
        color="offWhite"
      >
        <MenuList>
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleSelect(option.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 'fit-content',
                px: 1.5,
                py: 1,
              }}
            >
              <StatusChip label={option.label} color={option.color} />
            </MenuItem>
          ))}
        </MenuList>
      </Popper>
    </Box>
  );
}

const MenuList = styled(Box)(({ theme }) => ({
  minWidth: 180,
  width: 'max-content',
  backgroundColor: theme.palette.custom.offWhite,
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  padding: '8px 0',
}));

export default TaskStatusSelectField;
